import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { PeriodoFiltro, TipoPeriodo } from '../../../models/dashboard/periodo-filtro.model';
import {
  pad,
  fmtDateTime,
  parseDateTime,
  calcularPeriodoPredefinido,
  PRESETS_PERIODO_PADRAO
} from '../../utils/periodo.utils';
import { PeriodoFiltroPersistenciaService, PeriodoFiltroEstado } from './periodo-filtro-persistencia.service';

interface SelectItem { label: string; value: any; }

@Component({
  selector: 'app-periodo-filtro',
  templateUrl: './periodo-filtro.component.html',
  styleUrls: ['./periodo-filtro.component.scss']
})
export class PeriodoFiltroComponent implements OnInit, OnDestroy {

  /**
   * Modo de operacao do componente.
   * - 'simples': renderiza p-dropdown direto (como dashboard-contas)
   * - 'avancado': renderiza button que abre dialog com tabs (como dashboard-header)
   */
  @Input() modo: 'simples' | 'avancado' = 'simples';

  /**
   * Lista de TipoPeriodo que o dropdown deve exibir como opcoes.
   * Default: PRESETS_PERIODO_PADRAO (lista completa de 9 presets).
   */
  @Input() opcoes: TipoPeriodo[] = PRESETS_PERIODO_PADRAO;

  /**
   * Valor inicial selecionado no dropdown.
   */
  @Input() valorPadrao: TipoPeriodo = 'thisMonth';

  /**
   * Placeholder do dropdown no modo simples.
   */
  @Input() placeholder = '';

  /**
   * Classe CSS adicional aplicada ao dropdown (modo simples) ou ao button (modo avancado).
   */
  @Input() styleClass = '';

  /**
   * Texto exibido no button do modo avancado.
   */
  @Input() textoExibicao = '';

  /**
   * Liga/desliga a persistencia GLOBAL do ultimo periodo escolhido
   * (localStorage). Default false (opt-in: a tela precisa habilitar).
   *
   * A persistencia e GLOBAL: TODAS as instancias que ligam `persistir`
   * compartilham UMA UNICA chave (igual ao shared_preferences global do
   * mobile). "Usou numa tela -> salva; abriu outra -> usa o mesmo periodo."
   *
   * - Quando true: ao aplicar um filtro, o periodo e salvo na chave global;
   *   no `ngOnInit`, se houver valor salvo, ele e restaurado e RE-EMITIDO
   *   (sobrescrevendo o fallback do pai).
   * - Quando false: comportamento atual, sem persistencia e sem emissao no
   *   init (ex.: Extrato de Caixa, que abre sempre em "Hoje").
   */
  @Input() persistir = false;

  /**
   * Emite o PeriodoFiltro calculado quando o usuario seleciona uma opcao.
   */
  @Output() periodoChanged = new EventEmitter<PeriodoFiltro>();

  /**
   * Emitido quando o dialog de filtro e aberto (modo avancado).
   */
  @Output() dialogAberto = new EventEmitter<void>();

  /**
   * Emitido quando o dialog de filtro e fechado (modo avancado).
   */
  @Output() dialogFechado = new EventEmitter<void>();

  // Estado compartilhado (ambos os modos)
  dropdownOptions: SelectItem[] = [];
  selectedValue!: TipoPeriodo;
  periodoLabels!: Record<TipoPeriodo, string>;

  // Texto do periodo padrao calculado na inicializacao (usado como fallback no botao do modo avancado)
  textoPadrao = '';

  // Traducoes do dialog (modo avancado)
  texts = {
    tipo_de_filtro: '',
    subtitulo_filtro: '',
    periodos: '',
    pre_definido: '',
    selecao: '',
    horario_inicial: '',
    horario_final: '',
    data_hora_inicial: '',
    data_hora_final: '',
    placeholder_data_hora: '',
    data_hora_obrigatoria: '',
    erro_fim_antes_inicio: '',
    concluir: ''
  };

  // Estado exclusivo do modo avancado
  showFilterDialog = false;
  filterType: 'periods' | 'monthDay' = 'monthDay';
  predefStartTime: Date = this.makeTime(0, 0);
  predefEndTime: Date = this.makeTime(23, 59);
  showPredefTimePickers = false;
  months: SelectItem[] = [];

  /**
   * Aba "Periodos": data e hora de cada extremo num UNICO p-calendar (showTime).
   * null = campo vazio (usuario apagou) -> "Concluir" fica desabilitado.
   */
  periodoInicio: Date | null = null;
  periodoFim: Date | null = null;

  /**
   * Datas-base do seletor quando o campo esta vazio: ao escolher um dia no
   * calendario a hora sugerida e 00:00 (inicio) / 23:59 (fim), e nao a hora atual.
   */
  readonly defaultInicio: Date = this.makeTime(0, 0);
  readonly defaultFim: Date = this.makeTime(23, 59);

  /**
   * Marca qual foi o ultimo modo aplicado pelo usuario.
   * - 'periods': usuario aplicou um intervalo customizado (mes/dia/ano) na tab "Periodos"
   * - 'monthDay': usuario aplicou um pre-definido ou mes na tab "Pre-definido"
   * - null: nenhum filtro foi aplicado pelo dialog ainda (primeira abertura)
   *
   * Usado em initAdvancedState() para decidir se devemos preservar os campos
   * de Periodos (caso 'periods') ou derivar do selectedValue (demais casos).
   */
  private lastAppliedFilterType: 'periods' | 'monthDay' | null = null;

  /**
   * Chave GLOBAL unica de persistencia, compartilhada por todas as instancias
   * que ligam `persistir`. Namespaceada pelo servico como `periodo-filtro:global`.
   */
  private static readonly PERSIST_KEY_GLOBAL = 'global';

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private persistencia: PeriodoFiltroPersistenciaService,
  ) {}

  ngOnInit(): void {
    this.selectedValue = this.valorPadrao;
    this.loadTranslations();
    this.buildDropdownOptions();
    this.calcularTextoPadrao();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildDropdownOptions();
      this.calcularTextoPadrao();
    });

    // Inicializar estado do modo avancado
    if (this.modo === 'avancado') {
      this.initAdvancedState();
    }

    // Persistencia GLOBAL: se houver periodo salvo, restaura e RE-EMITE de
    // forma DEFERIDA para (a) evitar ExpressionChangedAfterItHasBeenChecked e
    // (b) rodar DEPOIS da init do pai — assim o valor persistido sobrescreve
    // o fallback que o pai calculou. Sem valor salvo: nao emite nada.
    this.restaurarPersistido();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // --- Modo Simples ---

  onPeriodoSelect(event: any): void {
    const tipo: TipoPeriodo = event.value;
    this.selectedValue = tipo;
    const today = new Date();
    const result = calcularPeriodoPredefinido(tipo, today, this.periodoLabels, 0, 0, 23, 59);

    const periodo: PeriodoFiltro = {
      tipo,
      dataInicio: fmtDateTime(result.start),
      dataFim: fmtDateTime(result.end),
      texto: result.texto
    };

    // Mantem o rotulo em sincronia com o periodo escolhido (simetria com applyFilter).
    this.textoPadrao = result.texto;

    // No modo simples o usuario so escolhe presets (sem aba "Periodos").
    this.salvarEstadoPersistido(periodo, 'monthDay');
    this.periodoChanged.emit(periodo);
  }

  // --- Modo Avancado ---

  openFilter(): void {
    // Bug #5: sincronizar o estado do modo Periodos com o pre-definido ativo ao abrir o dialog
    this.initAdvancedState();
    this.showFilterDialog = true;
    this.onMonthDayChange();
    // Reassignar os Date para forcar o p-calendar a renderizar o valor no input
    this.predefStartTime = new Date(this.predefStartTime.getTime());
    this.predefEndTime = new Date(this.predefEndTime.getTime());
    this.periodoInicio = this.periodoInicio ? new Date(this.periodoInicio.getTime()) : null;
    this.periodoFim = this.periodoFim ? new Date(this.periodoFim.getTime()) : null;
    // Bug #3: notificar componente pai que o dialog foi aberto
    this.dialogAberto.emit();
  }

  onMonthDayChange(): void {
    // Oculta os time pickers nos presets que usam o instante atual como limite
    // (untilToday/untilMonthEnd) ou datas fixas (showAll) — neles o horario nao
    // e configuravel (ver periodo.utils.ts).
    this.showPredefTimePickers =
      this.selectedValue !== 'untilToday' &&
      this.selectedValue !== 'untilMonthEnd' &&
      this.selectedValue !== 'showAll';
  }

  /** Aba "Periodos": os dois campos estao preenchidos. */
  get periodosPreenchidos(): boolean {
    return !!this.periodoInicio && !!this.periodoFim;
  }

  /** Aba "Periodos": fim anterior ao inicio (so avalia com os dois preenchidos). */
  get periodoFimAntesDoInicio(): boolean {
    return this.periodosPreenchidos && this.periodoFim!.getTime() < this.periodoInicio!.getTime();
  }

  /** Habilita o botao "Concluir". A aba "Pre-definido" nao tem restricao nova. */
  get podeConcluir(): boolean {
    if (this.filterType !== 'periods') {
      return true;
    }
    return this.periodosPreenchidos && !this.periodoFimAntesDoInicio;
  }

  applyFilter(): void {
    let start: Date;
    let end: Date;
    let texto = '';

    if (this.filterType === 'monthDay') {
      const today = new Date();

      const sh = this.predefStartTime.getHours();
      const sm = this.predefStartTime.getMinutes();
      const eh = this.predefEndTime.getHours();
      const em = this.predefEndTime.getMinutes();
      const result = calcularPeriodoPredefinido(
        this.selectedValue, today, this.periodoLabels, sh, sm, eh, em
      );
      start = result.start;
      end = result.end;
      texto = result.texto;
    } else {
      // Defesa: o botao ja fica desabilitado com campo vazio.
      if (!this.periodoInicio || !this.periodoFim) {
        return;
      }
      // Copia descartando segundos/ms: o contrato emitido e dd/MM/yyyyTHH:mm.
      start = this.semSegundos(this.periodoInicio);
      end = this.semSegundos(this.periodoFim);
      texto = `${this.rotuloData(start)} - ${this.rotuloData(end)}`;
    }

    // Bug #2: bloquear emissao se intervalo invalido (inicio > fim)
    if (start > end) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('dashboard.filtros.erro_intervalo_titulo'),
        detail: this.translate.instant('dashboard.filtros.erro_intervalo_detalhe'),
        life: 4000,
      });
      return;
    }

    const periodo: PeriodoFiltro = {
      tipo: this.selectedValue,
      dataInicio: fmtDateTime(start),
      dataFim: fmtDateTime(end),
      texto
    };

    // Sincroniza o rotulo do botao com o periodo aplicado. Sem isso o botao ficaria
    // preso no texto de `valorPadrao` quando o pai nao controla `textoExibicao` nem
    // usa `persistir`. `textoExibicao` (se vindo do pai) tem prioridade no template,
    // entao atualizar `textoPadrao` nao interfere nesses casos.
    this.textoPadrao = texto;

    // Registrar qual tab foi aplicada para que a proxima abertura preserve o estado.
    this.lastAppliedFilterType = this.filterType;

    // Persistir o periodo aplicado (preset ou intervalo manual) na chave global.
    this.salvarEstadoPersistido(periodo, this.filterType);

    this.showFilterDialog = false;
    // Bug #3: notificar componente pai que o dialog foi fechado
    this.dialogFechado.emit();
    this.periodoChanged.emit(periodo);
  }

  // --- Metodos privados ---

  /**
   * Calcula o texto descritivo do periodo padrao para exibicao no botao do modo avancado.
   * Usado como fallback quando a pagina pai ainda nao forneceu textoExibicao.
   */
  private calcularTextoPadrao(): void {
    if (!this.periodoLabels) {
      this.textoPadrao = '';
      return;
    }

    const result = calcularPeriodoPredefinido(this.valorPadrao, new Date(), this.periodoLabels, 0, 0, 23, 59);
    this.textoPadrao = result.texto;
  }

  /** Copia a data zerando segundos e milissegundos (campos LOCAIS, sem fuso). */
  private semSegundos(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), 0, 0);
  }

  /** Rotulo do botao no formato historico da aba "Periodos": dd/Mes/aaaa. */
  private rotuloData(d: Date): string {
    const mes = this.months.find(x => x.value === d.getMonth() + 1)?.label || pad(d.getMonth() + 1);
    return `${pad(d.getDate())}/${mes}/${d.getFullYear()}`;
  }

  private makeTime(h: number, m: number): Date {
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  }

  private initAdvancedState(): void {
    const now = new Date();
    this.months = [
      { label: 'Jan', value: 1 }, { label: 'Fev', value: 2 }, { label: 'Mar', value: 3 },
      { label: 'Abr', value: 4 }, { label: 'Mai', value: 5 }, { label: 'Jun', value: 6 },
      { label: 'Jul', value: 7 }, { label: 'Ago', value: 8 }, { label: 'Set', value: 9 },
      { label: 'Out', value: 10 }, { label: 'Nov', value: 11 }, { label: 'Dez', value: 12 }
    ];

    // Bug UX: preencher campos de Periodos com base no filtro vigente para que o
    // usuario sempre veja o que aplicou. Cobre 3 casos:
    //  1) Ultimo filtro aplicado foi "Periodos" -> preservar periodoInicio/periodoFim
    //  2) Selecionado um pre-definido (TipoPeriodo) -> derivar via calcularPeriodoPredefinido
    //  3) Sem filtro vigente -> 01/01 do ano atual 00:00 -> ultimo dia do mes atual 23:59
    if (this.lastAppliedFilterType === 'periods' && this.periodoInicio && this.periodoFim) {
      // Caso 1: preservar valores ja aplicados na tab Periodos.
      return;
    }

    if (this.selectedValue && this.periodoLabels) {
      // Caso 2: pre-definido (TipoPeriodo) selecionado.
      const sh = this.predefStartTime.getHours();
      const sm = this.predefStartTime.getMinutes();
      const eh = this.predefEndTime.getHours();
      const em = this.predefEndTime.getMinutes();
      const { start, end } = calcularPeriodoPredefinido(
        this.selectedValue, now, this.periodoLabels, sh, sm, eh, em
      );
      this.periodoInicio = this.semSegundos(start);
      this.periodoFim = this.semSegundos(end);
    } else {
      // Caso 3: primeira abertura sem nada selecionado.
      this.periodoInicio = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      this.periodoFim = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 0, 0);
    }
  }

  private loadTranslations(): void {
    this.periodoLabels = {
      untilToday: this.translate.instant('dashboard.filtros.ate_hoje'),
      yesterdayAndToday: this.translate.instant('dashboard.filtros.ontem_e_hoje'),
      thisWeek: this.translate.instant('dashboard.filtros.esta_semana'),
      thisMonth: this.translate.instant('dashboard.filtros.este_mes'),
      untilMonthEnd: this.translate.instant('dashboard.filtros.ate_fim_do_mes'),
      prevMonth: this.translate.instant('dashboard.filtros.mes_anterior'),
      nextMonth: this.translate.instant('dashboard.filtros.proximo_mes'),
      last30: this.translate.instant('dashboard.filtros.ultimos_30_dias'),
      today: this.translate.instant('dashboard.filtros.hoje'),
      yesterday: this.translate.instant('dashboard.filtros.ontem'),
      showAll: this.translate.instant('dashboard.filtros.mostrar_todos'),
      custom: ''
    };

    if (this.modo === 'avancado') {
      this.texts = {
        tipo_de_filtro: this.translate.instant('dashboard.texts.tipo_de_filtro'),
        subtitulo_filtro: this.translate.instant('dashboard.texts.subtitulo_filtro'),
        periodos: this.translate.instant('dashboard.texts.periodos'),
        pre_definido: this.translate.instant('dashboard.texts.pre_definido'),
        selecao: this.translate.instant('dashboard.texts.selecao'),
        horario_inicial: this.translate.instant('dashboard.texts.horario_inicial'),
        horario_final: this.translate.instant('dashboard.texts.horario_final'),
        data_hora_inicial: this.translate.instant('dashboard.texts.data_hora_inicial'),
        data_hora_final: this.translate.instant('dashboard.texts.data_hora_final'),
        placeholder_data_hora: this.translate.instant('dashboard.texts.placeholder_data_hora'),
        data_hora_obrigatoria: this.translate.instant('dashboard.texts.data_hora_obrigatoria'),
        erro_fim_antes_inicio: this.translate.instant('dashboard.texts.erro_fim_antes_inicio'),
        concluir: this.translate.instant('dashboard.texts.concluir')
      };
    }
  }

  private buildDropdownOptions(): void {
    this.dropdownOptions = this.opcoes.map(tipo => ({
      label: this.periodoLabels[tipo] || tipo,
      value: tipo
    }));
  }

  // --- Persistencia GLOBAL (localStorage) ---

  /**
   * Salva o periodo aplicado na chave GLOBAL (somente se `persistir` ligado).
   */
  private salvarEstadoPersistido(periodo: PeriodoFiltro, filterType: 'periods' | 'monthDay'): void {
    if (!this.persistir) {
      return;
    }
    const estado: PeriodoFiltroEstado = {
      tipo: periodo.tipo,
      filterType,
      dataInicio: periodo.dataInicio,
      dataFim: periodo.dataFim,
      texto: periodo.texto,
    };
    this.persistencia.salvar(PeriodoFiltroComponent.PERSIST_KEY_GLOBAL, estado);
  }

  /**
   * Restaura (se houver) o ultimo periodo salvo na chave GLOBAL e RE-EMITE de
   * forma deferida. Com `persistir` desligado ou sem valor salvo: nao faz nada
   * (fallback do pai prevalece).
   */
  private restaurarPersistido(): void {
    if (!this.persistir) {
      return;
    }
    const estado = this.persistencia.carregar(PeriodoFiltroComponent.PERSIST_KEY_GLOBAL);
    if (!estado) {
      return;
    }

    // Reidratar estado do componente.
    this.selectedValue = estado.tipo as TipoPeriodo;
    this.filterType = estado.filterType;

    let periodo: PeriodoFiltro;

    const ehPresetRelativo =
      estado.filterType === 'monthDay' &&
      typeof estado.tipo === 'string' &&
      estado.tipo !== 'custom';

    if (ehPresetRelativo && this.periodoLabels) {
      // Preset relativo: RECOMPUTAR as datas (nao restaurar datas velhas).
      const result = calcularPeriodoPredefinido(
        estado.tipo as TipoPeriodo, new Date(), this.periodoLabels, 0, 0, 23, 59
      );
      periodo = {
        tipo: estado.tipo,
        dataInicio: fmtDateTime(result.start),
        dataFim: fmtDateTime(result.end),
        texto: result.texto,
      };
      this.lastAppliedFilterType = 'monthDay';
    } else {
      // Intervalo manual (aba "Periodos") ou 'custom': usar as datas SALVAS.
      periodo = {
        tipo: estado.tipo,
        dataInicio: estado.dataInicio,
        dataFim: estado.dataFim,
        texto: estado.texto,
      };
      this.lastAppliedFilterType = estado.filterType;
      // Aba "Periodos": reidratar os campos com as datas salvas para que, ao
      // reabrir o dialog, o usuario veja exatamente o intervalo aplicado.
      if (estado.filterType === 'periods') {
        this.periodoInicio = parseDateTime(estado.dataInicio);
        this.periodoFim = parseDateTime(estado.dataFim);
      }
    }

    // Atualizar o texto do botao (modo avancado) para refletir o restaurado.
    this.textoExibicao = periodo.texto;
    this.textoPadrao = periodo.texto;

    // Realinhar o estado interno do dialog avancado com o periodo restaurado.
    if (this.modo === 'avancado') {
      this.initAdvancedState();
    }

    // Emissao DEFERIDA: roda depois da init do pai e evita ExpressionChanged.
    Promise.resolve().then(() => this.periodoChanged.emit(periodo));
  }
}
